import { Pencil } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";

import { QueryErrorCard } from "@/components/query-error-card";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccountPermissions } from "@/lib/api";

import { ConnectionKit } from "./connection-kit";
import { ContactList } from "./contact-list";
import { EditAccountDialog } from "./edit-account-dialog";
import { PermissionGrid } from "./permission-grid";

export function AdminAccountDetailPage() {
  const { accountId } = useParams();
  const permissionsQuery = useAccountPermissions(accountId);
  const data = permissionsQuery.data;
  const [editOpen, setEditOpen] = useState(false);

  // SDK errors carry the HTTP status (see api/client.ts), so we can single out
  // an unknown account (404) from a transient failure worth retrying.
  const errorStatus = (permissionsQuery.error as { status?: number } | null)?.status;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-muted text-xs font-medium tracking-[0.18em] uppercase">
            Customer account
          </p>
          {data && (
            <StatusPill tone={data.mcp_enabled ? "green" : "neutral"}>
              {data.mcp_enabled ? "MCP enabled" : "MCP disabled"}
            </StatusPill>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-foreground text-2xl font-normal tracking-tight sm:text-3xl">
            {data?.account_name ?? "Account"}
          </h2>
          {data && accountId && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </div>
        <p className="text-muted mt-2 max-w-2xl text-sm leading-relaxed">
          Manage this customer&apos;s contacts, share their connection kit, and tune what each
          person may do through MCP.
        </p>
      </header>

      {permissionsQuery.isLoading ? (
        <div className="space-y-8">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      ) : permissionsQuery.isError ? (
        errorStatus === 404 ? (
          <QueryErrorCard
            message="This account could not be found."
            backTo="/admin/accounts"
            backLabel="Back to accounts"
          />
        ) : (
          <QueryErrorCard
            message="Could not load this account."
            onRetry={() => void permissionsQuery.refetch()}
            backTo="/admin/accounts"
            backLabel="Back to accounts"
          />
        )
      ) : data && accountId ? (
        <div className="space-y-8">
          <ContactList accountId={accountId} contacts={data.contacts} />
          <ConnectionKit account={data} />
          <section className="space-y-3">
            <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
              Permissions
            </h3>
            <p className="text-muted -mt-1 max-w-2xl text-sm leading-relaxed">
              Account defaults apply to everyone unless a person has an explicit override; cleared
              cells inherit the default.
            </p>
            <PermissionGrid accountId={accountId} data={data} />
          </section>
          <EditAccountDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            accountId={accountId}
            accountName={data.account_name}
            segment={data.segment}
            region={data.region}
          />
        </div>
      ) : null}
    </div>
  );
}
